import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import DataTableComponent from "../../../components/Tenant/DataTableComponent/DataTableComponent";
import { useFetchQrScanTimelineMutation } from "../../../features/tenant/qrScanTimeline/qrScanTimelineApi";
import QrScanTimelineDetailModal from "./QrScanTimelineDetailModal";
import { HiOutlineSearch, HiOutlineEye } from "react-icons/hi";

const HIT_STATUS_OPTIONS = [
  { value: "1", label: "Processed" },
  { value: "2", label: "Pending" },
  { value: "0", label: "Not Found" },
  { value: "-1", label: "Already Locked" },
  { value: "3", label: "Already Scanned" },
];

const TYPE_OPTIONS = [
  { value: "main", label: "Main" },
  { value: "scheme", label: "Scheme" },
  { value: "both", label: "Both" },
];

const STATUS_STYLE = {
  Processed: "bg-green-100 text-green-700 border-green-200",
  Pending: "bg-yellow-100 text-yellow-700 border-yellow-200",
  "Not Found": "bg-red-100 text-red-700 border-red-200",
  "Already Locked": "bg-gray-100 text-gray-700 border-gray-200",
  "Already Scanned": "bg-blue-100 text-blue-700 border-blue-200",
};

const TYPE_STYLE = {
  main: "bg-blue-50 text-blue-700 border-blue-200",
  scheme: "bg-purple-50 text-purple-700 border-purple-200",
  both: "bg-indigo-50 text-indigo-700 border-indigo-200",
};

const QrScanTimeline = () => {
  const initialFilters = {
    userId: "",
    mobile: "",
    type: "",
    status: "",
    startDate: "",
    endDate: "",
  };

  const [filters, setFilters] = useState(initialFilters);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);

  const [showDetail, setShowDetail] = useState(false);
  const [selectedHitId, setSelectedHitId] = useState(null);

  const [fetchTimeline, { isLoading }] = useFetchQrScanTimelineMutation();

  const getAuth = () => ({
    token: sessionStorage.getItem("access_token"),
    tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
  });

  const handleFetchData = async (currentPage = page, currentLimit = limit, currentFilters = filters) => {
    try {
      const { token, tenant_id } = getAuth();
      const response = await fetchTimeline({
        token,
        tenant_id,
        body: {
          userId: currentFilters.userId ? Number(currentFilters.userId) : undefined,
          mobile : currentFilters.mobile && currentFilters.mobile.length > 0 ? String(currentFilters.mobile).trim() : undefined,
          type: currentFilters.type || undefined,
          status: currentFilters.status || undefined,
          startDate: currentFilters.startDate || undefined,
          endDate: currentFilters.endDate || undefined,
          page: currentPage,
          limit: currentLimit,
        },
      }).unwrap();

      if (response.status) {
        setData(response.body.data || []);
        setTotal(response.body.total || 0);
      } else {
        toast.error(response.message || "Failed to fetch scan timeline");
      }
    } catch (error) {
      console.error("Fetch timeline error:", error);
      toast.error("Something went wrong while fetching scan timeline");
    }
  };

  useEffect(() => {
    handleFetchData();
  }, []);

  const handlePageChange = (pageNumber) => {
    setPage(pageNumber);
    handleFetchData(pageNumber, limit, filters);
  };

  const handlePerRowsChange = (newLimit, currentPage) => {
    setLimit(newLimit);
    setPage(1);
    handleFetchData(1, newLimit, filters);
  };

  const onSearch = (e) => {
    e.preventDefault();
    setPage(1);
    handleFetchData(1, limit, filters);
  };

  const onReset = () => {
    setFilters(initialFilters);
    setPage(1);
    handleFetchData(1, limit, initialFilters);
  };

  const statusBadge = (label) => (
    <span
      className={`px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider border ${
        STATUS_STYLE[label] || "bg-gray-100 text-gray-700 border-gray-200"
      }`}
    >
      {label}
    </span>
  );

  const typeBadge = (type) => (
    <span
      className={`px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider border ${
        TYPE_STYLE[type] || "bg-gray-100 text-gray-700 border-gray-200"
      }`}
    >
      {type}
    </span>
  );

  const columns = [
    {
      name: "S.No",
      selector: (_, i) => (page - 1) * limit + i + 1,
      width: "70px",
    },
    {
      name: "User",
      cell: (row) => (
        <div className="py-2">
          <p className="font-bold text-gray-900">{row.user_name || "N/A"}</p>
          <p className="text-xs text-gray-500">{row.user_mobile}</p>
        </div>
      ),
      minWidth: "180px",
    },
    {
      name: "Type",
      cell: (row) => typeBadge(row.type),
      width: "110px",
      center: true,
    },
    {
      name: "Status",
      cell: (row) => statusBadge(row.status_label),
      minWidth: "150px",
    },
    {
      name: "QR Count",
      selector: (row) => row.count,
      width: "110px",
      center: true,
    },
    {
      name: "Location",
      cell: (row) => (
        <span className="text-sm text-gray-500">
          {[row.city, row.state].filter(Boolean).join(", ") || "—"}
        </span>
      ),
      minWidth: "150px",
    },
    {
      name: "Scanned At",
      selector: (row) => new Date(row.created_at).toLocaleString(),
      sortable: true,
      minWidth: "180px",
    },
    {
      name: "Actions",
      cell: (row) => (
        <button
          onClick={() => {
            setSelectedHitId(row.id);
            setShowDetail(true);
          }}
          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors group"
          title="View Timeline"
        >
          <HiOutlineEye className="w-5 h-5 transition-transform group-hover:scale-110" />
        </button>
      ),
      width: "90px",
      center: true,
    },
  ];

  const inputClass =
    "block w-full px-4 py-2 text-sm text-gray-900 border border-gray-200 rounded-xl bg-gray-50/50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-white transition-all outline-none";

  return (
    <div className="min-h-screen bg-gray-50/50">
      {/* Page header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30 px-6 shadow-sm">
        <div className="border-b bg-white border-gray-300 py-2 shadow-sm text-xl font-bold">
          QR Scan Timeline
          <LinksComponent />
        </div>
      </div>

      <div className="p-4 max-w-[1600px] mx-auto space-y-6">
        {/* Filters */}
        <div className="bg-white p-4 rounded-md shadow-sm border border-gray-100">
          <form
            onSubmit={onSearch}
            className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 items-end"
          >
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-500 ml-1">User ID</label>
              <input
                type="number"
                className={inputClass}
                placeholder="Filter by user ID..."
                value={filters.userId}
                onChange={(e) => setFilters({ ...filters, userId: e.target.value })}
              />
            </div>
             <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-500 ml-1">Mobile</label>
              <input
                type="number"
                className={inputClass}
                placeholder="Filter by mobile..."
                value={filters.mobile}
                onChange={(e) => setFilters({ ...filters, mobile: e.target.value })}
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-500 ml-1">Scan Type</label>
              <select
                className={inputClass}
                value={filters.type}
                onChange={(e) => setFilters({ ...filters, type: e.target.value })}
              >
                <option value="">All Types</option>
                {TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-500 ml-1">Status</label>
              <select
                className={inputClass}
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <option value="">All Statuses</option>
                {HIT_STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-500 ml-1">Start Date</label>
              <input
                type="date"
                className={inputClass}
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-500 ml-1">End Date</label>
              <input
                type="date"
                className={inputClass}
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              />
            </div>

            <div className="flex gap-2">
              <button
                type="submit"
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl transition-all shadow-md shadow-blue-100 active:scale-95 h-[42px]"
              >
                <HiOutlineSearch className="w-4 h-4" />
                Search
              </button>
              <button
                type="button"
                onClick={onReset}
                className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-600 font-bold rounded-xl transition-all h-[42px] text-sm"
              >
                Reset
              </button>
            </div>
          </form>
        </div>

        {/* Table */}
        <div className="bg-white rounded-md shadow-sm border border-gray-100 overflow-hidden">
          <DataTableComponent
            columns={columns}
            data={data}
            loading={isLoading}
            totalRows={total}
            handlePageChange={handlePageChange}
            handlePerRowsChange={handlePerRowsChange}
            paginationServer
            highlightOnHover
            customStyles={{
              table: { style: { backgroundColor: "white" } },
              rows: {
                style: {
                  minHeight: "64px",
                  "&:not(:last-of-type)": { borderBottomColor: "#f3f4f6" },
                },
              },
              headRow: {
                style: {
                  backgroundColor: "#f9fafb",
                  borderBottomColor: "#e5e7eb",
                  minHeight: "52px",
                },
              },
              headCells: {
                style: {
                  fontSize: "10px",
                  fontWeight: "900",
                  color: "#9ca3af",
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                },
              },
            }}
          />
        </div>
      </div>

      <QrScanTimelineDetailModal
        show={showDetail}
        setShow={setShowDetail}
        hitId={selectedHitId}
      />
    </div>
  );
};

export default QrScanTimeline;
